import React, { useState, useRef, useCallback, useEffect } from 'react';
import useSharePdf from '../hooks/useSharePdf';

// The panel's minimum width (its `min-w-[260px]`) and the gap kept from the viewport edge —
// used to decide whether the panel must open from the trigger's RIGHT edge.
export const SHARE_MENU_MIN_WIDTH_PX = 260;
export const SHARE_MENU_EDGE_PAD_PX = 8;

/**
 * SharePdfButton — generates a PDF from rendered React content, then shows a
 * dropdown menu with Download / Print / Share actions.
 *
 * 🔀 **GENERATION AND THE THREE ACTIONS MOVED TO `hooks/useSharePdf.js` ON
 * 2026-08-10 — an EXTRACTION, so this component's rendered DOM is unchanged.**
 * It kept `menuOpen` and all the JSX; the hook took `generatePdf`,
 * `ensureCurrentVariant`, the extras/cache state and the Download / Print /
 * Share handlers. Nothing about this component's props, markup, classes or
 * behaviour moved, and no caller was touched.
 * 🔑 The reason for the split is at the hook: the new order actions menu has
 * to offer Print WITHOUT nesting this dropdown inside another one, which is
 * broken both by `ui/Dropdown`'s `overflow-y-auto` clip and by the two
 * document-level `mousedown` close listeners fighting. Read that docblock
 * before re-inlining any of it.
 *
 * SITE-DEFAULT LOOK (apply to every new caller for visual consistency):
 *   <SharePdfButton
 *     renderContent={...} filename={...} title={...}
 *     orientation="landscape"  // (or "portrait" — depends on layout)
 *     dropDirection="down"     // dropdown opens BELOW the button by default
 *     className={BTN.secondary}  // from constants/buttons.js
 *   >
 *     <svg ...download icon../>
 *     Download / Print
 *     <svg ...chevron-down../>
 *   </SharePdfButton>
 *
 *   Don't add a separate standalone Print button next to this — the dropdown
 *   exposes Download, Print, and Share in one place. Risk Assessments,
 *   Budget Overview, and Budget Cost Codes all follow this pattern as of
 *   2026-05-31.
 *
 * Two API shapes:
 *
 *   (a) New `extras` API — supports multiple toggleable add-ons:
 *
 *     <SharePdfButton
 *       extras={[
 *         { key: 'signOnSheet',     label: 'sign-on sheet',          filenameSuffix: '-with-signon' },
 *         { key: 'reviewQuestions', label: 'pre/post review',        filenameSuffix: '-with-review' },
 *       ]}
 *       renderContent={({ activeExtras }) => (
 *         <PrintLayout
 *           includeSignOnSheet={activeExtras.signOnSheet}
 *           includeReviewQuestions={activeExtras.reviewQuestions}
 *         />
 *       )}
 *       filename="thing.pdf" />
 *
 *     Each `extras` entry renders a checkbox in the dropdown. Active keys are
 *     passed to `renderContent` as `activeExtras`. Filename gets each active
 *     extra's `filenameSuffix` appended in declaration order.
 *
 *     Pass `extrasOptional` when `renderContent` always produces a complete
 *     document by itself and the extras are just optional add-ons (e.g. RA:
 *     the base assessment always renders, sign-on / review are trailing
 *     sections). Without it, the actions are disabled until ≥1 extra is
 *     ticked ("Select at least one section to include").
 *
 *   (b) Legacy single-alternate API — kept for back-compat with non-RA callers:
 *
 *     <SharePdfButton
 *       renderContent={() => <Layout />}
 *       alternateRenderContent={() => <Layout includeSignOnSheet />}
 *       alternateLabel="sign-on sheet"
 *       alternateFilenameSuffix="-with-signon" />
 *
 *     Internally wrapped into a single-element extras list. When the legacy
 *     checkbox is on, `alternateRenderContent` is used in place of `renderContent`.
 */
function SharePdfButton({
  renderContent, filename, title, className, children,
  orientation = 'portrait', dropDirection = 'up',
  extras: extrasProp = null,
  // When true, the extras are purely optional add-ons and `renderContent`
  // always produces a complete document on its own (e.g. Risk Assessments,
  // where the base RA always renders and sign-on / review are trailing
  // extras). This disables the "select at least one section" guard so the
  // base document can be downloaded/printed with no extras ticked. Leave
  // false for callers whose ONLY content comes from extras (e.g. Budget
  // Overview's renderPdfMakeDoc returns null when nothing is selected).
  extrasOptional = false,
  // Legacy single-alternate API
  alternateRenderContent = null,
  alternateLabel = '',
  alternateFilenameSuffix = '',
  // Optional pdfmake path — when provided AND returns a non-null doc-def
  // for the current activeExtras selection, the PDF is built via pdfmake
  // (vector text, selectable / searchable). Otherwise falls through to
  // the html2pdf image path. Lets a caller opt into text PDFs for
  // specific extra combinations while keeping the legacy raster path
  // for other combinations. Lazy-loaded — pdfmake/vfs are only imported
  // when this prop is actually exercised.
  renderPdfMakeDoc = null,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // 🔑 A stable callback, so the hook's `finish` (and the three action handlers
  // that depend on it) do not get a new identity on every render.
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const {
    generating,
    pendingAction,
    activeExtras,
    setActiveExtras,
    extras,
    nothingActive,
    canShare,
    handleDownload,
    handlePrint,
    handleShare,
    resetCache,
  } = useSharePdf({
    renderContent,
    filename,
    title,
    orientation,
    extras: extrasProp,
    extrasOptional,
    alternateRenderContent,
    alternateLabel,
    alternateFilenameSuffix,
    renderPdfMakeDoc,
    onActionComplete: closeMenu,
  });

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setMenuOpen(false);
        resetCache();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen, resetCache]);

  // Opening the menu is cheap — no PDF generation here. The user picks their
  // extras inside the dropdown, then clicking Download / Print / Share is what
  // commits the chosen options and triggers `generatePdf` via the action
  // handlers below (each of which calls `ensureCurrentVariant`).
  // 🪤 OFF-SCREEN FIX (owner 2026-10-28, Cost Reports' Actions ▾ near the right edge): the
  // panel always opened from its LEFT edge at min 260px, so a trigger near the right of the
  // window pushed it past the viewport and the page grew a horizontal scrollbar. Open it from
  // the RIGHT edge whenever there is not room to the right of the trigger.
  const [alignRight, setAlignRight] = useState(false);
  const handleClick = useCallback((e) => {
    if (e) e.stopPropagation();
    if (generating) return;
    const r = buttonRef.current ? buttonRef.current.getBoundingClientRect() : null;
    setAlignRight(!!r && r.left + SHARE_MENU_MIN_WIDTH_PX > window.innerWidth - SHARE_MENU_EDGE_PAD_PX);
    setMenuOpen(true);
  }, [generating]);

  return (
    <div className="relative w-full md:w-auto">
      <button
        ref={buttonRef}
        onClick={handleClick}
        disabled={generating}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className={`w-full md:w-auto ${className}`}
        title={generating ? 'Generating PDF...' : 'Download, print, or share as PDF'}
      >
        {generating ? (
          <>
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Generating…
          </>
        ) : children}
      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          data-testid="share-pdf-menu"
          data-align={alignRight ? 'right' : 'left'}
          className={`absolute ${dropDirection === 'down' ? 'top-full mt-1' : 'bottom-full mb-1'} ${alignRight ? 'right-0' : 'left-0'} max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 min-w-[260px] py-1`}
          onClick={(e) => e.stopPropagation()}
        >
          {extras.map((x, idx) => (
            <label
              key={x.key}
              className={`flex items-center gap-2 px-3 py-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 select-none ${idx === extras.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''}`}
              title="If checked, all download/print/share actions below include this extra section."
            >
              <input
                type="checkbox"
                checked={!!activeExtras[x.key]}
                onChange={(e) => setActiveExtras((prev) => ({ ...prev, [x.key]: e.target.checked }))}
                disabled={generating}
                className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              Include {x.label}
            </label>
          ))}
          {/* Guard against generating an empty PDF: when extras are declared
              and none are active, disable the action buttons and surface a
              hint. extras.length === 0 means legacy mode (always content via
              renderContent()) and the actions stay enabled. */}
          {nothingActive && (
            <div className="px-3 py-2 text-[11px] italic text-amber-700 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-900/20 border-b border-gray-100 dark:border-gray-700">
              Select at least one section to include.
            </div>
          )}
          <button
            onClick={handleDownload}
            disabled={!!pendingAction || nothingActive}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download PDF
            {pendingAction === 'download' && (
              <span className="text-[10px] text-gray-400 ml-auto">Generating…</span>
            )}
          </button>
          <button
            onClick={handlePrint}
            disabled={!!pendingAction || nothingActive}
            className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print…
            {pendingAction === 'print' && (
              <span className="text-[10px] text-gray-400 ml-auto">Generating…</span>
            )}
          </button>
          {canShare && (
            <button
              onClick={handleShare}
              disabled={!!pendingAction || nothingActive}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share…
              {pendingAction === 'share' && (
                <span className="text-[10px] text-gray-400 ml-auto">Generating…</span>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default SharePdfButton;
